/**
 * useRestrictedFeatureDialog Hook
 *
 * Provides state management for the RestrictedFeatureCTADialog.
 * Use this hook to show the CTA dialog when users try to access
 * features that require the desktop app in browser mode.
 */

import { useState, useCallback } from 'react';
import { isBrowserMode } from '../browserModeUtils';
import type { RestrictedFeature } from '../components/RestrictedFeatureCTADialog';

export interface UseRestrictedFeatureDialogResult {
  /** Whether the dialog is currently open */
  isOpen: boolean;
  /** The feature that triggered the dialog */
  feature: RestrictedFeature | null;
  /** Show the dialog for a specific feature */
  showDialog: (feature: RestrictedFeature) => void;
  /** Close the dialog */
  closeDialog: () => void;
  /**
   * Check if a feature should be blocked and show dialog if needed.
   * Returns true if the feature was blocked (dialog shown), false if allowed.
   */
  checkAndBlock: (feature: RestrictedFeature) => boolean;
}

/**
 * Hook to manage the restricted feature CTA dialog.
 *
 * @example
 * ```tsx
 * const { checkAndBlock, isOpen, feature, closeDialog } = useRestrictedFeatureDialog();
 *
 * const handleTerminalClick = () => {
 *   if (checkAndBlock('terminal')) return;
 *   // ... continue with terminal logic
 * };
 *
 * return (
 *   <>
 *     <button onClick={handleTerminalClick}>Terminal</button>
 *     <RestrictedFeatureCTADialog
 *       open={isOpen}
 *       feature={feature || 'terminal'}
 *       onClose={closeDialog}
 *     />
 *   </>
 * );
 * ```
 */
export function useRestrictedFeatureDialog(): UseRestrictedFeatureDialogResult {
  const [isOpen, setIsOpen] = useState(false);
  const [feature, setFeature] = useState<RestrictedFeature | null>(null);

  const showDialog = useCallback((feat: RestrictedFeature) => {
    setFeature(feat);
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    // Keep feature set for dialog close animation
  }, []);

  const checkAndBlock = useCallback(
    (feat: RestrictedFeature): boolean => {
      if (!isBrowserMode()) {
        return false; // Allow in desktop mode
      }

      showDialog(feat);
      return true; // Blocked in browser mode
    },
    [showDialog]
  );

  return {
    isOpen,
    feature,
    showDialog,
    closeDialog,
    checkAndBlock,
  };
}
